export type SurveyFieldType =
  'text' | 'email' | 'number' | 'date' | 'select' | 'textarea' | 'checkbox';

export interface SurveyField {
  name: string;
  question: string;
  type: SurveyFieldType;
  required?: boolean;
  placeholder?: string;
  options?: string[] | { value: string; label: string }[];
  defaultValue?: string | number | boolean;
  rows?: number;
  group: 'personal' | 'race' | 'experience' | 'schedule' | 'health';
  validators?: {
    onChange?: (value: any) => string | undefined;
    onSubmit?: (value: any) => string | undefined;
  };
}

export const SURVEY_FIELDS: SurveyField[] = [
  // --- Personal ---
  {
    name: 'age',
    question: 'How old are you?',
    type: 'number',
    required: true,
    group: 'personal',
    validators: {
      onSubmit: (value) => {
        if (!value) return 'Age is required';
        if (value < 18 || value > 120) return 'Please enter a valid age';
        return undefined;
      },
    },
  },
  {
    name: 'gender',
    question: 'What is your gender?',
    type: 'select',
    required: true,
    group: 'personal',
    options: [
      { value: '', label: 'Select...' },
      { value: 'Male', label: 'Male' },
      { value: 'Female', label: 'Female' },
      { value: 'Non-binary', label: 'Non-binary' },
      { value: 'Prefer not to say', label: 'Prefer not to say' },
    ],
    validators: {
      onSubmit: (value) => (!value ? 'Gender is required' : undefined),
    },
  },

  // --- Race ---
  {
    name: 'race_date',
    question: 'When is your race scheduled?',
    type: 'date',
    required: true,
    group: 'race',
    validators: {
      onSubmit: (value) => {
        if (!value) return 'Race date is required';
        return undefined;
      },
    },
  },
  {
    name: 'race_name',
    question: 'What is the name of your upcoming race?',
    type: 'text',
    required: true,
    group: 'race',
    placeholder: 'Start typing a race name',
    validators: {
      onSubmit: (value) => (!value ? 'Race name is required' : undefined),
    },
  },
  {
    name: 'race_distance',
    question: 'What is the distance of your race?',
    type: 'select',
    required: true,
    group: 'race',
    options: [
      { value: '', label: 'Select...' },
      { value: '5K', label: '5K' },
      { value: '10K', label: '10K' },
      { value: 'Half Marathon', label: 'Half Marathon' },
      { value: 'Marathon', label: 'Marathon' },
      { value: 'Ultra Marathon', label: 'Ultra Marathon' },
      { value: 'Other', label: 'Other' },
    ],
    validators: {
      onSubmit: (value) => (!value ? 'Race distance is required' : undefined),
    },
  },
  {
    name: 'goal_time',
    question: 'What is your goal time for this race?',
    type: 'text',
    required: true,
    group: 'race',
    placeholder: 'e.g., 3:30:00 or 3 hours 30 minutes',
    validators: {
      onSubmit: (value) => (!value ? 'Goal time is required' : undefined),
    },
  },

  // --- Experience ---
  {
    name: 'years_running',
    question: 'How many years have you been running?',
    type: 'select',
    defaultValue: 'Less than 1 year',
    group: 'experience',
    options: [
      'Less than 1 year',
      '1-2 years',
      '3-5 years',
      'More than 5 years',
    ],
  },
  {
    name: 'previous_marathons',
    question: 'How many marathons have you completed?',
    type: 'select',
    defaultValue: '0',
    group: 'experience',
    options: [
      { value: '0', label: 'This will be my first' },
      { value: '1-3', label: '1-3' },
      { value: '4-10', label: '4-10' },
      { value: '10+', label: 'More than 10' },
    ],
  },
  {
    name: 'pr_5k',
    question: 'What is your current 5K personal record? (if any)',
    type: 'text',
    group: 'experience',
    placeholder: 'e.g., 22:30 or N/A',
  },
  {
    name: 'pr_10k',
    question: 'What is your current 10K personal record? (if any)',
    type: 'text',
    group: 'experience',
    placeholder: 'e.g., 47:00 or N/A',
  },
  {
    name: 'pr_half_marathon',
    question: 'What is your current half marathon personal record? (if any)',
    type: 'text',
    group: 'experience',
    placeholder: 'e.g., 1:45:00 or N/A',
  },
  {
    name: 'pr_marathon',
    question: 'What is your current marathon personal record? (if any)',
    type: 'text',
    group: 'experience',
    placeholder: 'e.g., 3:45:00 or N/A',
  },
  {
    name: 'weekly_mileage',
    question:
      'What is your average weekly running mileage over the past month?',
    type: 'select',
    defaultValue: '0-10 miles',
    group: 'experience',
    options: [
      '0-10 miles',
      '10-20 miles',
      '20-30 miles',
      '30-40 miles',
      '40-50 miles',
      'More than 50 miles',
    ],
  },

  // --- Schedule ---
  {
    name: 'training_days_per_week',
    question: 'How many days per week do you want to train?',
    type: 'select',
    defaultValue: '4',
    group: 'schedule',
    options: [
      { value: '3', label: '3 days' },
      { value: '4', label: '4 days' },
      { value: '5', label: '5 days' },
      { value: '6', label: '6 days' },
      { value: '7', label: '7 days' },
    ],
  },
  {
    name: 'long_run_day',
    question: 'Which day do you prefer for your long runs?',
    type: 'select',
    defaultValue: 'Saturday',
    group: 'schedule',
    options: [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ],
  },
  {
    name: 'weekday_time',
    question: 'How much time can you dedicate to training on weekdays?',
    type: 'select',
    defaultValue: '30-60 minutes',
    group: 'schedule',
    options: [
      'Less than 30 minutes',
      '30-60 minutes',
      '60-90 minutes',
      'More than 90 minutes',
    ],
  },
  {
    name: 'weekend_time',
    question: 'How much time can you dedicate to training on weekends?',
    type: 'select',
    defaultValue: 'More than 3 hours',
    group: 'schedule',
    options: [
      'Less than 1 hour',
      '1-2 hours',
      '2-3 hours',
      'More than 3 hours',
    ],
  },
  {
    name: 'preferred_terrain',
    question: 'What terrain do you prefer to train on?',
    type: 'select',
    defaultValue: 'Road',
    group: 'schedule',
    options: ['Road', 'Trail', 'Track', 'Treadmill', 'Mixed'],
  },
  {
    name: 'cross_training',
    question:
      'Do you do any cross-training activities? (select all that apply)',
    type: 'text',
    group: 'schedule',
    placeholder: 'e.g., cycling, swimming, strength training, yoga, none',
  },

  // --- Health ---
  {
    name: 'has_injuries',
    question:
      'Do you have any current injuries or medical conditions that could affect your training?',
    type: 'select',
    defaultValue: 'false',
    group: 'health',
    options: [
      { value: 'false', label: 'No' },
      { value: 'true', label: 'Yes' },
    ],
  },
  {
    name: 'recent_injury_history',
    question:
      'Have you had any injuries in the last 6 months? (even if currently healed)',
    type: 'select',
    defaultValue: 'false',
    group: 'health',
    options: [
      { value: 'false', label: 'No' },
      { value: 'true', label: 'Yes' },
    ],
  },
  {
    name: 'sleep_hours',
    question: 'On average, how many hours of sleep do you get per night?',
    type: 'select',
    defaultValue: '7-8 hours',
    group: 'health',
    options: [
      'Less than 6 hours',
      '6-7 hours',
      '7-8 hours',
      '8-9 hours',
      'More than 9 hours',
    ],
  },
  {
    name: 'training_intensity',
    question: "What's your desired training intensity?",
    type: 'select',
    defaultValue: 'Moderate',
    group: 'health',
    options: ['Easy', 'Moderate', 'Intense'],
  },
  {
    name: 'additional_info',
    question:
      'Is there any other information you would like us to consider when creating your training plan?',
    type: 'textarea',
    group: 'health',
    placeholder:
      'e.g., specific terrain, elevation, weather conditions, equipment available...',
    rows: 3,
  },
];

export const SURVEY_DEFAULT_VALUES: Record<string, string | number | boolean> =
  Object.fromEntries(
    SURVEY_FIELDS.map((f) => [
      f.name,
      f.defaultValue !== undefined
        ? f.defaultValue
        : f.type === 'checkbox'
          ? false
          : f.type === 'number'
            ? ''
            : '',
    ]),
  );

export const SURVEY_GROUPS: {
  id: SurveyField['group'];
  label: string;
  description: string;
}[] = [
  {
    id: 'race',
    label: 'Your Race',
    description: 'Start with the race you are training for',
  },
  {
    id: 'personal',
    label: 'About You',
    description: 'Basic info to personalize your plan',
  },
  {
    id: 'experience',
    label: 'Running Experience',
    description: 'Your history and current fitness level',
  },
  {
    id: 'schedule',
    label: 'Training Schedule',
    description: 'When and how you want to train',
  },
  {
    id: 'health',
    label: 'Health & Recovery',
    description: 'Keep you training safely',
  },
];
